import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'

const Terms = () => {
  const { t } = useTranslation()

  return (
    <div className="bg-dark-950 min-h-screen py-12 relative overflow-hidden">
      <div className="fixed inset-0 bg-grid-pattern opacity-20"></div>
      <div className="absolute inset-0 bg-gradient-mesh opacity-30"></div>

      <div className="container-custom relative z-10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-10"
          >
            <h1 className="text-4xl md:text-5xl font-black mb-4">
              <span className="text-gradient">{t('terms.title')}</span>
            </h1>
            <div className="w-24 h-0.5 bg-gradient-to-r from-transparent via-primary-500 to-transparent mx-auto"></div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="space-y-8"
          >
            {/* Allgemeine Geschäftsbedingungen (AGB) */}
            <section className="card-hover p-6 sm:p-8">
              <h2 className="text-2xl md:text-3xl font-bold mb-6 text-gradient">
                {t('terms.agb.title')}
              </h2>
              <div className="space-y-4 text-base text-gray-300 leading-relaxed">
                <p className="text-gray-400 mb-4">
                  {t('terms.agb.intro')}
                </p>
                
                <ul className="space-y-3 list-disc list-inside ml-4">
                  <li>{t('terms.agb.point1')}</li>
                  <li>{t('terms.agb.point2')}</li>
                  <li>{t('terms.agb.point3')}</li>
                  <li>{t('terms.agb.point4')}</li>
                  <li>{t('terms.agb.point5')}</li>
                  <li>{t('terms.agb.point6')}</li>
                </ul>

                <div className="mt-6 p-4 bg-primary-500/10 border-l-4 border-primary-500 rounded-r-lg">
                  <p className="text-gray-200 font-medium">
                    {t('terms.agb.summary')}
                  </p>
                </div>
              </div>
            </section>

            {/* Widerrufsbelehrung */}
            <section className="card-hover p-6 sm:p-8">
              <h2 className="text-2xl md:text-3xl font-bold mb-6 text-gradient">
                {t('terms.widerruf.title')}
              </h2>
              <div className="space-y-4 text-base text-gray-300 leading-relaxed">
                <div className="mb-4 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <p className="text-red-400 font-semibold mb-2">
                    {t('terms.widerruf.noReturn')}
                  </p>
                  <p className="text-gray-300">
                    {t('terms.widerruf.reason')}
                  </p>
                </div>

                <ul className="space-y-3 list-disc list-inside ml-4 mb-6">
                  <li>{t('terms.widerruf.point1')}</li>
                  <li>{t('terms.widerruf.point2')}</li>
                  <li>{t('terms.widerruf.point3')}</li>
                </ul>

                <div className="p-4 bg-yellow-500/10 border-l-4 border-yellow-500 rounded-r-lg">
                  <p className="text-gray-200 font-medium mb-2">
                    {t('terms.widerruf.noticeTitle')}
                  </p>
                  <p className="text-gray-300">
                    {t('terms.widerruf.notice')}
                  </p>
                </div>

                <div className="mt-6 p-4 bg-dark-800/50 rounded-lg border border-dark-700">
                  <p className="text-gray-300 leading-relaxed">
                    {t('terms.widerruf.example')}
                  </p>
                </div>
              </div>
            </section>

            {/* Wichtige Punkte */}
            <section className="card-hover p-6 sm:p-8">
              <h2 className="text-2xl md:text-3xl font-bold mb-6 text-gradient">
                {t('terms.important.title')}
              </h2>
              <div className="space-y-4 text-base text-gray-300 leading-relaxed">
                <p className="text-gray-400 mb-4">
                  {t('terms.important.intro')}
                </p>

                <ul className="space-y-3 list-disc list-inside ml-4">
                  <li>{t('terms.important.point1')}</li>
                  <li>{t('terms.important.point2')}</li>
                  <li>{t('terms.important.point3')}</li>
                </ul>

                <div className="mt-6 p-5 bg-gradient-to-br from-primary-500/10 to-purple-500/10 border border-primary-500/30 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      className="mt-1 w-5 h-5 rounded border-gray-600 bg-dark-800 text-primary-500 focus:ring-primary-500 focus:ring-2"
                      disabled
                      checked
                      readOnly
                    />
                    <p className="text-gray-200 font-medium leading-relaxed">
                      {t('terms.important.checkbox')}
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Zusammenfassung */}
            <section className="card-hover p-6 sm:p-8 border-2 border-primary-500/30">
              <h2 className="text-2xl md:text-3xl font-bold mb-6 text-gradient">
                {t('terms.summary.title')}
              </h2>
              <div className="space-y-4 text-base text-gray-300 leading-relaxed">
                <ul className="space-y-3 list-none ml-0">
                  <li className="flex items-start space-x-3">
                    <span className="text-primary-400 font-bold mt-1">•</span>
                    <span><strong className="text-primary-400">{t('terms.summary.agb')}:</strong> {t('terms.summary.agbDesc')}</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-primary-400 font-bold mt-1">•</span>
                    <span><strong className="text-primary-400">{t('terms.summary.widerruf')}:</strong> {t('terms.summary.widerrufDesc')}</span>
                  </li>
                </ul>
              </div>
            </section>
          </motion.div>

          {/* Last Updated */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="mt-8 text-center text-sm text-gray-500"
          >
            <p>{t('terms.lastUpdated')}: {new Date().toLocaleDateString('de-DE')}</p>
          </motion.div>
        </div>
      </div>
    </div>
  )
}

export default Terms
